import {
  type EventName,
  META_KEYS,
  type Payload,
  type TelemetryMeta,
  type TelemetryRow,
} from './types';

export { probeEnv, presetOf, classifyBrowser, classifyInApp, osMajor } from './env';
export type { BrowserKind, EnvProbe, InAppKind, OsKind } from './env';
export { readIdentity } from './identity';
export type { Identity, UidSource } from './identity';
export { META_KEYS } from './types';
export type { EventName, MetaKey, Payload, Preset, TelemetryMeta, TelemetryRow } from './types';

/** payload 가 메타 키와 겹치는 이름들 — 있으면 버그다(그 값은 행에 실리지 않는다). */
export function metaCollisions(payload: Payload): string[] {
  const out: string[] = [];
  for (const k of META_KEYS) if (k in payload) out.push(k);
  return out;
}

/**
 * 행 조립 — **메타가 payload 를 이긴다.** 겹치는 payload 키는 버리고 메타를 앞에 둔다.
 * 순수 함수라 테스트가 시계 없이 검사한다.
 */
export function composeRow(meta: TelemetryMeta, payload: Payload): TelemetryRow {
  const body: Record<string, unknown> = {};
  for (const k of Object.keys(payload)) {
    if ((META_KEYS as readonly string[]).includes(k)) continue;
    body[k] = payload[k];
  }
  return { ...meta, ...body };
}

export interface TelemetryOptions {
  readonly url: string | null;
  readonly uid: string;
  readonly sid: string;
  readonly batchSize: number;
}

/**
 * 계측 실패가 게임 실패가 되면 안 된다 — 전 경로가 try/catch 다.
 * 카톡 인앱·프라이빗 모드에서 저장소 접근이 throw 하면 첫 track 에서 부트스트랩이 죽어
 * 그 참가자가 session_start 조차 남기지 못하고 표본에서 사라진다.
 */
export class Telemetry {
  private outbox: TelemetryRow[] = [];
  private seq = 0;
  private dropped = 0;

  constructor(private readonly opt: TelemetryOptions) {}

  track(name: EventName, payload: Payload, flushNow = false): void {
    try {
      this.seq += 1;
      const meta: TelemetryMeta = {
        n: name,
        i: this.seq,
        t: Date.now(),
        mt: Math.round(performance.now()),
        uid: this.opt.uid,
        sid: this.opt.sid,
      };
      const clash = metaCollisions(payload);
      if (clash.length > 0)
        console.warn('[tlm] payload 가 메타 키를 덮어쓰려 함 — 메타 우선', name, clash);
      this.outbox.push(composeRow(meta, payload));
      if (this.opt.url === null) console.debug('[tlm]', name, payload);
      if (flushNow || this.outbox.length >= this.opt.batchSize) this.flush();
    } catch (err) {
      this.dropped += 1;
      console.warn('[tlm] track 실패', err);
    }
  }

  /** sendBeacon(text/plain) → fetch keepalive 순으로 시도한다. 실패분은 아웃박스에 되돌린다. */
  flush(): void {
    if (this.outbox.length === 0) return;
    if (this.opt.url === null) {
      this.outbox = []; // 수집처가 없으면 무한 누적을 막는다(개발 빌드)
      return;
    }
    const batch = this.outbox;
    this.outbox = [];
    const body = JSON.stringify({ rows: batch, dropped: this.dropped });
    try {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      if (navigator.sendBeacon(this.opt.url, blob)) return;
    } catch {
      /* 아래 fetch 로 폴백 */
    }
    try {
      void fetch(this.opt.url, {
        method: 'POST',
        body,
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      }).catch(() => {
        this.outbox.unshift(...batch);
      });
    } catch {
      this.outbox.unshift(...batch);
    }
  }

  pending(): number {
    return this.outbox.length;
  }

  /** pagehide(persisted=false) 에서만 quit 을 합성한다 — bfcache 복귀를 이탈로 세지 않는다. */
  install(onHardExit: () => void): void {
    const onPageHide = (e: PageTransitionEvent): void => {
      try {
        if (!e.persisted) onHardExit();
      } catch {
        /* noop */
      }
      this.flush();
    };
    const onVis = (): void => {
      if (document.visibilityState === 'hidden') this.flush();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVis);
  }
}
